import React, { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { InputDatePicker } from '@/components/ui/InputDatePicker'
import { TimePicker } from '@/components/ui/timePicker'
import { EditIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { parseDate } from '@almaris/shared/utils/dates'

type EstateVisitUpdateFormValues = {
  visitDate: Date
  startTime: string
}

type EstateVisitUpdateButtonProps = {
  enabled: boolean
  currentStartTime: string
  visitSlotDuration: number // in minutes
  onSubmit: (startTime: Date, endTime: Date) => void
}

export const EstateVisitUpdateButton = ({
  enabled,
  currentStartTime,
  visitSlotDuration,
  onSubmit,
}: EstateVisitUpdateButtonProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Parse current times to get date and time components
  const currentStartDate = useMemo(() => {
    return parseDate(currentStartTime) || new Date()
  }, [currentStartTime])

  const schema = useMemo(
    () =>
      yup.object().shape({
        visitDate: yup.date().required(t('Forms.errors.required')),
        startTime: yup.string().required(t('Forms.errors.required')),
      }),
    [t]
  )

  const defaultValues = useMemo(() => {
    return {
      visitDate: currentStartDate,
      startTime: format(currentStartDate, 'HH:mm'),
    }
  }, [currentStartDate])

  const form = useForm<EstateVisitUpdateFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  const handleSubmit = async (values: EstateVisitUpdateFormValues) => {
    const { visitDate, startTime } = values

    // Create new start date with the selected date and time
    const newStartDate = new Date(visitDate)
    const [startHour, startMinute] = startTime.split(':').map(Number)
    newStartDate.setHours(startHour, startMinute, 0, 0)

    // Calculate end date by adding visitSlotDuration minutes
    const newEndDate = new Date(newStartDate)
    newEndDate.setMinutes(newEndDate.getMinutes() + visitSlotDuration)

    onSubmit(newStartDate, newEndDate)
    setOpen(false)
    form.reset(defaultValues)
  }

  const handleCancel = () => {
    setOpen(false)
    form.reset(defaultValues)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={!enabled}
          onClick={() => setOpen(true)}
        >
          <EditIcon className="tw-h-4 tw-w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="tw-min-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('EstateVisitUpdateButton.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="tw-space-y-6"
          >
            <FormField
              control={form.control}
              name="visitDate"
              render={({ field }) => (
                <FormItem className="tw-flex tw-flex-col">
                  <FormLabel>
                    {t('EstateVisitUpdateButton.visitDate')}
                  </FormLabel>
                  <InputDatePicker
                    {...field}
                    fromYear={new Date().getFullYear()}
                    toYear={new Date().getFullYear() + 2}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="startTime"
              render={({ field }) => (
                <FormItem className="tw-flex tw-flex-col tw-w-full">
                  <FormLabel>
                    {t('EstateVisitUpdateButton.startTime')}
                  </FormLabel>
                  <TimePicker
                    {...field}
                    startTime="08:00"
                    endTime="21:00"
                    step={visitSlotDuration}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="tw-flex tw-justify-end tw-space-x-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="submit">{t('common.save')}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
